import { useRouter } from "next/router";
import { useUpdateEffect } from "react-use";

import { useEffect, useRef, useState } from "react";
import { SearchIcon, XIcon } from "icons/outline";
import { IconButton, Tooltip, useCallbackRef } from "ui";

const isMac =
  typeof navigator !== "undefined"
    ? /Mac|iPhone|iPod|iPad/.test(navigator.platform)
    : false;

export const GlobalSearch = ({ placeholder }: { placeholder: string }) => {
  const router = useRouter();
  const [search, setSearch] = useState("");
  const [showSearchTooltip, setShowSearchTooltip] = useState(false);
  const showSearchTooltipFirstTime = useRef(true);
  const tooltipTimerId = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useCommandK(() => inputRef.current?.focus());

  // Sync input state, if search query changed from outside
  useEffect(() => {
    if (!router.isReady) return;
    setSearch((router.query.search as string) || "");
  }, [router.isReady, router.query.search]);

  /**
   * Debounce updating URL to avoid multiple rerenders
   * reset page query on global search changes
   * remove search query param from url if has empty string value
   */
  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      const { search: _, page, ...restQuery } = router.query;

      if (search) {
        router.replace({
          query: { ...restQuery, search, ...(page ? { page: 1 } : {}) },
        });
      } else {
        router.replace({
          query: { ...restQuery, ...(page ? { page: 1 } : {}) },
        });
      }
    }, 450);

    return () => clearTimeout(timerId);
  }, [search]);

  const onInputFocus = () => {
    if (showSearchTooltipFirstTime.current) {
      setShowSearchTooltip(true);

      tooltipTimerId.current = setTimeout(() => {
        setShowSearchTooltip(false);

        tooltipTimerId.current && clearTimeout(tooltipTimerId.current);
      }, 2000);

      showSearchTooltipFirstTime.current = false;
    }
  };

  return (
    <div className="relative h-8 flex-1">
      <input
        ref={inputRef}
        placeholder={placeholder}
        className="peer h-full w-full rounded-md  border border-foundations-subtle bg-foundations-subtelst-02 pl-10 pr-11 placeholder:text-foundations-subtle focus:border-info-bold focus:ring-1 focus:ring-info-bold md:text-sm"
        name="Search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={onInputFocus}
      />
      <div className="absolute left-0 top-0 flex h-full items-center justify-center pl-2 text-foundations-subtler peer-focus:text-info">
        <Tooltip open={showSearchTooltip} onOpenChange={setShowSearchTooltip}>
          <Tooltip.Trigger render={<div />}>
            <SearchIcon className="size-5" />
          </Tooltip.Trigger>
          <Tooltip.Popup size="sm" variant="dark" side="bottom" align="start">
            Search using prefixes only
          </Tooltip.Popup>
        </Tooltip>
      </div>

      {search ? (
        <IconButton
          className="absolute right-2 top-1/2 -translate-y-1/2  p-0.5 peer-focus:text-info"
          onClick={() => setSearch("")}
          size="xs"
          variant="tertiary"
        >
          <XIcon />
        </IconButton>
      ) : (
        <div className="pointer-events-none absolute right-2 top-1/2 flex h-[22px] min-w-[38px] -translate-y-1/2 items-center justify-center rounded border border-foundations-subtle bg-foundations-subtlest px-0.5 text-sm text-foundations-subtle peer-focus:hidden">
          {isMac ? "⌘" : "Ctrl + "}K
        </div>
      )}
    </div>
  );
};

export function useGlobalSearchQuery() {
  const router = useRouter();
  return router.query.search;
}

function useCommandK(callback: () => void) {
  const callbackRef = useCallbackRef(callback);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "k" && e.key !== "K") return;

      // mac uses meta, others use ctrl
      if ((isMac && e.metaKey) || (!isMac && e.ctrlKey)) {
        e.preventDefault();
        callbackRef();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [callbackRef]);
}
